import {
  DATE_FILTERS,
  NO_VALUE_LABEL,
  STAGE_LABEL,
  capitalise,
  formatMoney,
  formatPhoneInput,
  hasValue,
  jobAddress,
  primaryPhone,
  type Cents,
  type DateFilter,
  type Job,
  type ListSummary,
} from "@roof-hawk/core";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { t, tn } from "../i18n";
import { navigate } from "../lib/hooks";

export const CHECK_ICON = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="3">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export function ListHead({
  title,
  count,
  dateFilter,
  onDateFilter,
  children,
}: {
  title: string;
  count: number;
  dateFilter?: DateFilter;
  onDateFilter?: (value: DateFilter) => void;
  children?: ReactNode;
}) {
  return (
    <div className="listhead">
      <h2>{title}</h2>
      <div className="lh-right">
        <span className="count">
          {count} {count === 1 ? t("record") : t("records")}
        </span>
        {dateFilter && onDateFilter && (
          <select
            className="datefilter"
            value={dateFilter}
            onChange={(e) => onDateFilter(e.target.value as DateFilter)}
          >
            {DATE_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {t(f.label)}
              </option>
            ))}
          </select>
        )}
        {children}
      </div>
    </div>
  );
}

/**
 * The shared job table used by Prospects, Jobs, Master and rep lists.
 *
 * The last column used to be the job number. It has come off every row of every
 * list and gone back to the places that identify a job — the job's own header,
 * the search box, a work order — because nobody scans a list for one. The
 * column it was using now answers the question the row was not: what is this
 * worth?
 *
 * `values` is contract dollars per job id, worked out by `contractAmount` from
 * `job_contract_parts`. It is `null` while it is still being fetched and the
 * column stays blank, because $0.00 that is about to change is worse than a
 * gap. Every cell is named, because a phone lays these six out in a different
 * order (see styles.css) and a nameless grid cell cannot be moved.
 */
export function JobRows({
  jobs,
  values,
}: {
  jobs: readonly Job[];
  values?: ReadonlyMap<string, Cents> | null;
}) {
  return (
    <div className="list">
      <div className="lrow head">
        <div>{t("Customer")}</div>
        <div className="hide">{t("Address")}</div>
        <div>{t("Bid")}</div>
        <div className="hide">{t("Status")}</div>
        <div className="hide">{t("Rep")}</div>
        <div className="lc-val">{t("Value")}</div>
      </div>
      {jobs.length === 0 ? (
        <div className="empty">
          <b>{t("Nothing here")}</b>
          {t("No records match the current filters.")}
        </div>
      ) : (
        jobs.map((job) => (
          <div
            key={job.id}
            className="lrow"
            onClick={() => navigate(`job=${job.id}`)}
          >
            <div className="lc-cust">
              <div className="cust">
                {job.contact?.full_name ?? "—"}
                {job.is_test && <span className="testbadge">{t("Test")}</span>}
              </div>
              <div className="sub">{primaryPhone(job.contact) ?? ""}</div>
            </div>
            <div className="lc-addr hide sub">{jobAddress(job)}</div>
            <div className="lc-bid">
              <span className={`pill ${job.bid_type}`}>
                {t(capitalise(job.bid_type))}
              </span>
            </div>
            <div className="lc-stage hide">
              <span className="pill">
                {t(STAGE_LABEL[job.stage] ?? job.stage)}
              </span>
            </div>
            <div className="lc-rep hide sub">{job.rep?.full_name ?? "—"}</div>
            <JobValueCell
              value={values?.get(job.id)}
              loaded={Boolean(values)}
            />
          </div>
        ))
      )}
    </div>
  );
}

function JobValueCell({
  value,
  loaded,
}: {
  value: Cents | undefined;
  loaded: boolean;
}) {
  if (!loaded) return <div className="lc-val" />;
  if (!hasValue(value))
    return <div className="lc-val none">{t(NO_VALUE_LABEL)}</div>;
  return <div className="lc-val">{formatMoney(value as Cents)}</div>;
}

/**
 * A row of figures above a list.
 *
 * Shared by the sold board's scoreboard and by the four figures over every
 * other list, so the two cannot drift into looking like different features.
 * An empty strip is a real state and not a mistake: the figures are money, the
 * money is fetched, and a zero that is about to change is worse than a blank.
 */
export function Scoreboard({ children }: { children?: ReactNode }) {
  return <div className="scoreboard">{children}</div>;
}

export function Figure({
  label,
  value,
  tone = "plain",
  note,
  flag,
}: {
  label: string;
  value: string;
  /** Counts are neither good nor bad news, so they stay black. */
  tone?: "good" | "bad" | "plain";
  note?: string | undefined;
  /** A job to do rather than a fact about the month. Green once it is clear. */
  flag?: "open" | "clear";
}) {
  const flagClass = flag ? ` flag${flag === "clear" ? " quiet" : ""}` : "";
  return (
    <div className={`sb-fig${flagClass}`}>
      <span className="k">{label}</span>
      <b className={tone}>{value}</b>
      {note && <span className="sb-note">{note}</span>}
    </div>
  );
}

/**
 * The four figures over a list that has not been sold yet.
 *
 * What is on the list, what it is worth, what has already been signed this
 * month, and how many customers nobody has called. `listSummary` in
 * packages/core decides which job lands in which figure; this only draws them.
 */
export function JobsSummary({ summary }: { summary: ListSummary | null }) {
  if (!summary) return <Scoreboard />;

  return (
    <Scoreboard>
      <Figure label={t("On this list")} value={String(summary.jobs)} />
      <Figure
        label={t("In the pipeline")}
        value={formatMoney(summary.pipeline)}
        note={tn(
          summary.pipelineJobs,
          "1 job not sold yet",
          "{n} jobs not sold yet",
        )}
      />
      <Figure
        label={t("Signed this month")}
        value={formatMoney(summary.signed)}
        tone={summary.signed > 0 ? "good" : "plain"}
        note={tn(summary.signedJobs, "1 job", "{n} jobs")}
      />
      <Figure
        label={t("Need a call")}
        value={String(summary.needCall)}
        flag={summary.needCall > 0 ? "open" : "clear"}
        note={
          summary.needCall > 0 ? t("Over 48 hours") : t("Nobody is waiting")
        }
      />
    </Scoreboard>
  );
}

/**
 * Hold the page behind a modal still.
 *
 * Without it the wheel scrolls the page under the overlay once the modal
 * itself has nothing left to scroll, so closing a work order can leave you
 * somewhere different from where you opened it — which is half of why the
 * editor felt like it went missing.
 *
 * The counter is the whole point, and it was not here before. Each modal used
 * to save `body.style.overflow` as it found it and put that value back on the
 * way out, which is correct for one modal and wrong for two: open a second
 * over the first and it records "hidden" as the value to restore, and if the
 * two then close in the other order — the outer one goes first, which happens
 * whenever finishing in a child modal closes the screen that opened it — the
 * last cleanup writes "hidden" back onto a page with no modal left on it. The
 * app is then unscrollable until it is reloaded, with nothing on screen to
 * explain why.
 *
 * That is the bug Samuel hit, and the reason nobody else did is that it only
 * bites on Android. iOS Safari has never honoured `overflow: hidden` on the
 * body for touch scrolling, so on an iPhone a stuck lock is invisible; Chrome
 * on Android honours it exactly, and the page simply stops moving.
 *
 * So the lock is counted, not saved and restored: the first modal takes it,
 * the last one out gives it back, and a cleanup that somehow runs twice is
 * ignored rather than double-counted.
 */
let modalsOpen = 0;
let scrollBeforeLock = "";

export function lockBodyScroll(): () => void {
  if (modalsOpen === 0) {
    scrollBeforeLock = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  modalsOpen += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    modalsOpen -= 1;
    if (modalsOpen === 0) document.body.style.overflow = scrollBeforeLock;
  };
}

export function Modal({
  title,
  subtitle,
  children,
  onClose,
  actions,
  wide,
  roomy,
}: {
  title: string;
  subtitle?: string | undefined;
  children: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  /** An editor with a table in it — as much of the window as it can have. */
  wide?: boolean;
  /**
   * A form that was cramped rather than a table that was clipped.
   *
   * Between the two, because they are different problems. The Present modal is
   * a column of label-and-control rows: at 560px the labels wrapped and the
   * checkbox captions ran to three lines, and at the 1400px `wide` gives a work
   * order it would be a line of text too long to read across. Jonathon,
   * 2026-08-24: "the present estimate pop up can be wider and easier to read."
   */
  roomy?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** The page behind stays where it is, and moves again afterwards. */
  useEffect(lockBodyScroll, []);

  /**
   * Rendered into <body>, never where it was written.
   *
   * `.modal-bg` is `position: fixed`, which normally means "against the
   * window". It stops meaning that the moment any ancestor has a `transform` —
   * the transformed element becomes the containing block and the overlay is
   * trapped inside it. The estimate card is exactly that: `.dpanel.show
   * .wide-card` is shifted with `translateX(-50%)` to break out of the page
   * column, so every modal opened from an estimate — Create Estimate, a line
   * item, an expense, Present — was being drawn inside the card, cut off at its
   * edges and pushed half a screen sideways. A portal puts the overlay back
   * where it belongs regardless of what it was opened from.
   */
  return createPortal(
    <div
      className="modal-bg"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        style={
          wide
            ? { maxWidth: "min(1400px, 96vw)" }
            : roomy
              ? { maxWidth: "min(880px, 94vw)" }
              : undefined
        }
      >
        {/* The way out, always on screen.

            Jonathon, 2026-08-19: "there needs to be an exit button on work
            orders, material orders other expenses." Those editors are a screen
            and a half long and their Done button is at the bottom of it, so
            leaving one meant scrolling past everything you had just done to
            find the way out. Escape and a click on the backdrop both closed it
            already; neither is discoverable, and neither exists on a phone.

            Sticky, so it stays put down a long editor, and it is the first
            thing in the modal so it is also the first thing a screen reader
            reaches. Nothing is lost by pressing it — every editor behind it
            saves as it goes. */}
        <div className="modal-head">
          <div className="modal-head-text">
            <h3>{title}</h3>
            {subtitle && <div className="subtle">{subtitle}</div>}
          </div>
          <button
            type="button"
            className="modal-x"
            aria-label={t("Close")}
            title={t("Close")}
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {children}
        {actions && <div className="actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * A phone box that punctuates itself.
 *
 * The dashes go in on every keystroke (`formatPhoneInput` in packages/core
 * decides where), which means the text under the caret can grow by a character
 * the user did not type. Left alone, the browser puts the caret back at the
 * same offset and it appears to jump backwards over the new dash — so the
 * caret is restored by counting digits rather than characters. Typing at the
 * end, the usual case, is unaffected; editing in the middle now behaves.
 */
export function PhoneInput({
  value,
  onChange,
  id,
  placeholder,
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <input
      ref={ref}
      id={id}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={(e) => {
        const raw = e.target.value;
        const caret = e.target.selectionStart ?? raw.length;
        const digitsBefore = raw.slice(0, caret).replace(/\D/g, "").length;
        const formatted = formatPhoneInput(raw);
        onChange(formatted);

        // After React has painted the punctuated value, put the caret back
        // after the same digit it was after before.
        requestAnimationFrame(() => {
          const input = ref.current;
          if (!input || document.activeElement !== input) return;
          let seen = 0;
          let position = digitsBefore === 0 ? 0 : formatted.length;
          for (let i = 0; i < formatted.length && digitsBefore > 0; i++) {
            if (!/\d/.test(formatted[i]!)) continue;
            seen++;
            if (seen === digitsBefore) {
              position = i + 1;
              break;
            }
          }
          // Step over a dash so the next keystroke lands on a digit.
          while (
            position < formatted.length &&
            !/\d/.test(formatted[position]!)
          )
            position++;
          input.setSelectionRange(position, position);
        });
      }}
    />
  );
}

/** A label/value row, the layout used all over the detail screens. */
export function Row({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="rrow">
      <span className="k">{label}</span>
      <span className="v">{children ?? "—"}</span>
    </div>
  );
}

export function Stub({ title, message }: { title: string; message: string }) {
  return (
    <div className="stub">
      <b>{title}</b>
      {message}
    </div>
  );
}

/**
 * A wheel that turns while something slow is running.
 *
 * Generating an estimate from the measurements takes several seconds, and a
 * greyed-out button reading "Generating…" looked like a button that had
 * stopped. Jonathon, 2026-10-21: "there needs to be a buffering wheel while
 * it's building the estimate so people know it's working on it."
 *
 * It is decoration: whatever it sits in already says what is happening in
 * words, so screen readers are told nothing extra.
 */
export function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

export function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="err" role="alert" style={{ margin: "8px 0" }}>
      {error}
    </div>
  );
}

/**
 * A text box that grows to fit what is in it, and can still be dragged.
 *
 * Every box in this app that holds prose — a scope of work, a work step, a
 * line's own name — was a fixed height, and most of them were one line tall.
 * What a rep typed was still there and still saved; they simply could not see
 * it, which on a scope of work means reading it three words at a time through
 * a slot. Jonathon, 2026-09-23: "we need to be able to click and drag the
 * scope of work box ... so we can see everything that's typed."
 *
 * So both. The box grows on its own as far as its content needs, and the
 * browser's own corner handle is left alone for anybody who wants it bigger or
 * smaller still. A box somebody has dragged keeps the size they gave it — a
 * height that jumps back the moment you type in it is not a size you chose.
 *
 * It only ever grows by itself. Shrinking as text is deleted sounds tidier and
 * is not: the box moves under the cursor while somebody is editing in it, and
 * the drag handle is right there for a box that has got too tall.
 *
 * **Except on a line item.** Jonathon, 2026-09-24: "the material order etc.
 * boxes don't need to be so big. they just need to look like one clean
 * row/line item with the option to make that box larger by dragging the
 * corner." A table row is read across, not down — see the "One line per row"
 * rule in docs/STATUS.md — and a product name that wraps to four lines in a
 * 200px column makes every row a paragraph. So `grow={false}` keeps the box
 * one line tall, showing the start of what is in it with the whole in its
 * tooltip, and leaves the corner handle for anybody who wants to see the
 * rest. Prose — a scope of work, a delivery note — keeps growing.
 */
export function GrowBox({
  className,
  onInput,
  grow = true,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /** False: one line tall until dragged, however much is in it. */
  grow?: boolean;
}) {
  const box = useRef<HTMLTextAreaElement>(null);
  const dragged = useRef(false);
  const before = useRef(0);

  const fit = () => {
    const el = box.current;
    if (!el || !grow || dragged.current) return;
    // `scrollHeight` is the content without the borders, so a box set to
    // exactly that is two pixels short and scrolls by one line for ever.
    const chrome = el.offsetHeight - el.clientHeight;
    if (el.scrollHeight + chrome > el.offsetHeight)
      el.style.height = `${el.scrollHeight + chrome}px`;
  };

  // On the way in as well as on every keystroke: a box opened on a scope
  // somebody wrote last week should already be showing all of it.
  useEffect(fit, [rest.value, grow]);

  return (
    <textarea
      {...rest}
      ref={box}
      className={["growbox", grow ? null : "line", className ?? null]
        .filter(Boolean)
        .join(" ")}
      onInput={(e) => {
        fit();
        onInput?.(e);
      }}
      // A drag of the corner handle is the only thing that changes the height
      // between a mouse going down on this box and coming back up.
      onMouseDown={() => {
        before.current = box.current?.offsetHeight ?? 0;
      }}
      onMouseUp={() => {
        if ((box.current?.offsetHeight ?? 0) !== before.current)
          dragged.current = true;
      }}
    />
  );
}
